import { useState, useEffect, useRef } from 'react';
import { useConfirmModal } from '@practium/ui';
import type {
	StateReferenceDTO,
	TextureSoil,
	DependentParam,
	FixedParam,
} from '../types/parameters.types';

interface UseParametersDraftProps {
	stateReference: StateReferenceDTO | null;
	selectedTexture: TextureSoil;
	onUpdate: (updated: StateReferenceDTO) => Promise<void>;
	onTextureChange: (texture: TextureSoil) => void;
}

export const useParametersDraft = ({
	stateReference,
	selectedTexture,
	onUpdate,
	onTextureChange,
}: UseParametersDraftProps) => {
	const { confirm, isOpen, options, handleConfirm, handleCancel: handleModalCancel } =
		useConfirmModal();

	const [draft, setDraft] = useState<StateReferenceDTO | null>(stateReference);
	const [localTexture, setLocalTexture] = useState<TextureSoil>(selectedTexture);
	const [tableResetKey, setTableResetKey] = useState(0);
	const [isDirty, setIsDirty] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const selectedTextureRef = useRef(selectedTexture);
	selectedTextureRef.current = selectedTexture;

	useEffect(() => {
		setDraft(stateReference);
		setLocalTexture(selectedTextureRef.current);
		setIsDirty(false);
		setTableResetKey((k) => k + 1);
	}, [stateReference]);

	const handleTextureChange = (texture: TextureSoil) => {
		setLocalTexture(texture);
		setIsDirty(true);
	};

	const handleDraftDependentParams = (params: DependentParam[]) => {
		setDraft((prev) => (prev ? { ...prev, dependentParams: params } : null));
		setIsDirty(true);
	};

	const handleDraftFixedParams = (params: FixedParam[]) => {
		setDraft((prev) => (prev ? { ...prev, fixedParams: params } : null));
		setIsDirty(true);
	};

	const handleSave = async () => {
		if (!draft) return;
		setIsSaving(true);
		try {
			await onUpdate(draft);
			onTextureChange(localTexture);
			setIsDirty(false);
		} finally {
			setIsSaving(false);
		}
	};

	const handleCancelEdit = async () => {
		const confirmed = await confirm({
			title: 'Descartar alterações',
			message: 'Você tem alterações não salvas. Deseja realmente cancelar?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (!confirmed) return;
		setDraft(stateReference);
		setLocalTexture(selectedTexture);
		setIsDirty(false);
		setTableResetKey((k) => k + 1);
	};

	const tabKey = `${stateReference?._id ?? 'no-ref'}-${tableResetKey}`;

	return {
		draft,
		localTexture,
		isDirty,
		isSaving,
		tabKey,
		handleTextureChange,
		handleDraftDependentParams,
		handleDraftFixedParams,
		handleSave,
		handleCancelEdit,
		confirmModal: { isOpen, options, handleConfirm, handleCancel: handleModalCancel },
	};
};
